import {
  type BasicManagerHubVisibilityParams,
  type ManagerCoachingVisibilityParams,
  type UseManagerCoachingSeatEligibilityOptions,
} from './types'
import { PRODUCT_TYPE } from '../common/constants/productType'
import { addUtcCalendarMonths } from '../common/utils/dateUtils'
import { MANAGING_TEAMS_TOPIC_ID } from '../essentials/constants'
import { isLanguageEnglish } from '../intl/utils/isLanguageEnglish'
import {
  getManagerCoachingRemainingSessions,
  MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS,
  type ManagerCoachingSeatEligibility,
} from '../managerCoaching/seatEligibility'

import type { PutUserPropertyRequest } from '../../models/user/User'

export const MANAGER_HUB_PROMOTION_SIGNAL_PROPERTY_NAME = 'promotionSignal.managerHub'

export const isManagingTeamsTopicDisabled = (disableSelfCareTopics?: string): boolean =>
  disableSelfCareTopics?.split(';').some((topicId) => topicId.trim() === MANAGING_TEAMS_TOPIC_ID) ?? false

const meetsSharedManagerHubRequirements = ({
  isManagerHubEnabled,
  productType,
  isUserMinor = false,
  language,
}: BasicManagerHubVisibilityParams | ManagerCoachingVisibilityParams): boolean =>
  isManagerHubEnabled && productType === PRODUCT_TYPE.WFMH && !isUserMinor && isLanguageEnglish(language ?? '')

export const shouldShowBasicManagerHub = (params: BasicManagerHubVisibilityParams): boolean =>
  meetsSharedManagerHubRequirements(params) &&
  params.isEssentialsEnabled === true &&
  !isManagingTeamsTopicDisabled(params.disableSelfCareTopics)

export const shouldShowManagerCoaching = (params: ManagerCoachingVisibilityParams): boolean =>
  meetsSharedManagerHubRequirements(params) && params.isInternationalUser === false

export const createManagerHubPromotionSignalRequestData = (timestamp: number): PutUserPropertyRequest => ({
  name: MANAGER_HUB_PROMOTION_SIGNAL_PROPERTY_NAME,
  value: String(timestamp),
  classType: 'String',
})

export const parseManagerHubPromotionSignalPropertyValue = (value: unknown): number | undefined => {
  if (typeof value !== 'string' || !/^\d+$/.test(value)) {
    return undefined
  }

  const timestamp = Number(value)
  return Number.isSafeInteger(timestamp) ? timestamp : undefined
}

export const isManagerHubPromotionSignalActive = (managerHubTimestamp: unknown, now: number = Date.now()): boolean => {
  if (
    typeof managerHubTimestamp !== 'number' ||
    !Number.isSafeInteger(managerHubTimestamp) ||
    managerHubTimestamp < 0 ||
    !Number.isSafeInteger(now) ||
    now < 0 ||
    managerHubTimestamp > now
  ) {
    return false
  }

  return now < addUtcCalendarMonths(managerHubTimestamp, 3)
}

/**
 * Preserve generic-card promotion for missing/ineligible seats and exhausted eligible seats.
 * Other eligible seats keep the default rank; loading and request failures never promote.
 */
export const canPromoteManagerHubBasedOnSeatEligibility = (seatEligibility?: ManagerCoachingSeatEligibility): boolean =>
  seatEligibility?.status === MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS.MISSING_LICENSE ||
  seatEligibility?.status === MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS.INELIGIBLE ||
  (seatEligibility?.status === MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS.ELIGIBLE &&
    getManagerCoachingRemainingSessions(seatEligibility.license) === 0)

/** Apply coaching prerequisites and signed-in identity before starting an app's seat loader. */
export const shouldFetchManagerCoachingSeatEligibility = ({
  shouldFetch = true,
  isUserLoggedIn,
  lyraId,
  ...visibility
}: ManagerCoachingVisibilityParams &
  UseManagerCoachingSeatEligibilityOptions & {
    isUserLoggedIn: boolean
    lyraId?: string
  }): boolean => shouldFetch && shouldShowManagerCoaching(visibility) && isUserLoggedIn && Boolean(lyraId)
